/**
 * BCP Document Transformation Utilities
 */

import type { BCPFormData } from "../types";
import type {
  ExtractedCompanyInfo,
  BCPGenerationResponse,
} from "../types/bcp-generation";
import { BCP_FIXED_SECTIONS } from "../constants/bcpFixedSections";
import {
  extractAndCleanSection,
  extractSectionByNumber,
  extractSectionFromParent,
  cleanMarkdownText,
} from "./markdownCleaner";

export interface BCPTransformOptions {
  extractedInfo?: ExtractedCompanyInfo;
  filename?: string;
}

/**
 * Transform BCP document string into structured form data
 */
export const transformBCPDocument = (
  bcpDocument: string,
  options: BCPTransformOptions = {},
): BCPFormData => {
  const { extractedInfo = {}, filename = "Sample Company" } = options;

  // Extract sections using existing utilities
  const companyInfo =
    extractSectionByNumber(bcpDocument, "1-1") ||
    extractAndCleanSection(bcpDocument, [
      "企業の基本情報",
      "基本情報",
      "会社概要",
    ]);

  const section1_1 = extractSectionByNumber(bcpDocument, "1-1") || "";

  // Extract business scope from extracted info or section
  let businessScope = extractedInfo.business_content || "";
  if (!businessScope) {
    const businessContentMatch = section1_1.match(
      /\*\*事業内容\*\*:\s*([^\n*]+(?:\n(?!\*\*)[^\n*]*)*)/,
    );
    if (businessContentMatch) {
      businessScope = cleanMarkdownText(businessContentMatch[1]);
    }
  }

  // Extract employee info
  let employeeInfo = "";
  const clientsMatch = section1_1.match(
    /\*\*主な取引先\*\*:\s*((?:\n-[^\n]+)*)/,
  );
  if (clientsMatch) {
    employeeInfo = cleanMarkdownText(clientsMatch[1]);
  }

  // Extract facility info
  let facilityInfo = "";
  let mgmtResourcesSection = "";
  if (bcpDocument.includes("基本版 復旧方針書")) {
    mgmtResourcesSection = extractSectionFromParent(
      bcpDocument,
      ["基本版 復旧方針書"],
      "2-1",
    );
  }
  if (!mgmtResourcesSection || mgmtResourcesSection.length < 50) {
    mgmtResourcesSection = extractAndCleanSection(bcpDocument, [
      "主要な経営資源の洗い出し",
      "【建物・設備】",
    ]);
  }

  const facilityMatch = mgmtResourcesSection.match(
    /\*\*【建物・設備】\*\*\s*((?:\s*\*\*[^*]+\*\*\s*(?:\n-[^\n]+)*\s*(?:\n☑[^\n]+)*\s*(?:\n☑[^\n]+)*)*)/,
  );
  if (facilityMatch) {
    facilityInfo = cleanMarkdownText(facilityMatch[1]);
  }

  // Build company info with extracted data
  const fullCompanyInfo =
    companyInfo ||
    buildFallbackCompanyInfo(
      extractedInfo,
      filename,
      facilityInfo,
      employeeInfo,
      businessScope,
    );

  // Extract other sections
  const continuityPolicy =
    extractSectionByNumber(bcpDocument, "1-2") ||
    extractAndCleanSection(bcpDocument, [
      "事業継続の基本方針",
      "基本方針",
      "継続方針",
    ]);

  // Risk analysis (avoid confusion with basic version 2-1)
  let riskAnalysis = "";
  const basicSectionStart = bcpDocument.indexOf("基本版 復旧方針書");
  if (basicSectionStart > 0) {
    const beforeBasicSection = bcpDocument.substring(0, basicSectionStart);
    riskAnalysis = extractSectionByNumber(beforeBasicSection, "2-1");
  } else {
    riskAnalysis = extractSectionByNumber(bcpDocument, "2-1");
  }
  if (!riskAnalysis) {
    riskAnalysis = extractAndCleanSection(bcpDocument, [
      "リスクの特定",
      "リスク分析",
      "想定リスク",
    ]);
  }

  // Continue with other sections...
  const criticalBusinesses =
    extractSectionByNumber(bcpDocument, "3-1") ||
    extractAndCleanSection(bcpDocument, ["重要業務の特定", "重要業務"]);

  const recoveryPlan =
    extractSectionByNumber(bcpDocument, "3-2") ||
    extractAndCleanSection(bcpDocument, [
      "業務復旧目標時間",
      "復旧計画",
      "復旧目標",
    ]);

  const financialPlan =
    extractSectionByNumber(bcpDocument, "3-3") ||
    extractAndCleanSection(bcpDocument, ["資金計画", "資金調達", "保険活用"]);

  const stockpilePlan =
    extractSectionByNumber(bcpDocument, "3-4") ||
    extractAndCleanSection(bcpDocument, ["備蓄計画", "備蓄", "非常用品"]);

  const alternativeMeasures =
    extractSectionByNumber(bcpDocument, "4-1") ||
    extractAndCleanSection(bcpDocument, [
      "緊急対応の代替策",
      "代替手段",
      "代替策",
    ]);

  const activationCriteria =
    extractSectionByNumber(bcpDocument, "5-1") ||
    extractSectionByNumber(bcpDocument, "5") ||
    extractAndCleanSection(bcpDocument, ["BCP発動基準", "発動基準"]);

  const emergencyOrganization =
    extractSectionByNumber(bcpDocument, "6-1") ||
    extractAndCleanSection(bcpDocument, ["緊急対応フロー", "初動対応"]);

  const rolesResponsibilities =
    extractSectionByNumber(bcpDocument, "6-2") ||
    extractAndCleanSection(bcpDocument, ["役割と責任", "組織体制", "責任者"]);

  const communicationMethods =
    extractSectionByNumber(bcpDocument, "6-3") ||
    extractAndCleanSection(bcpDocument, ["緊急時の連絡手段", "連絡手段"]);

  const regularTraining =
    extractSectionByNumber(bcpDocument, "7-1") ||
    extractAndCleanSection(bcpDocument, ["定期訓練", "訓練", "避難訓練"]);

  const bcpReview =
    extractSectionByNumber(bcpDocument, "7-2") ||
    extractAndCleanSection(bcpDocument, ["BCPの定期見直し", "定期見直し"]);

  const employeeEducation =
    extractSectionByNumber(bcpDocument, "7-3") ||
    extractAndCleanSection(bcpDocument, ["従業員教育", "研修", "教育"]);

  // Extract employee count for communication methods template
  let employeeCount: number | undefined;

  // Try extractedInfo first
  if (extractedInfo.employees) {
    const match = extractedInfo.employees.match(/(\d+)/);
    if (match) {
      employeeCount = parseInt(match[1], 10);
    }
  }

  // Try fullCompanyInfo if not found
  if (employeeCount === undefined && fullCompanyInfo) {
    const match = fullCompanyInfo.match(/従業員数[:：]\s*(\d+)/);
    if (match) {
      employeeCount = parseInt(match[1], 10);
    }
  }

  return {
    companyInfo: fullCompanyInfo,
    continuityPolicy: continuityPolicy || getDefaultContinuityPolicy(),
    riskAnalysis: riskAnalysis || getDefaultRiskAnalysis(),
    criticalBusinesses: criticalBusinesses || getDefaultCriticalBusinesses(),
    recoveryPlan: recoveryPlan || getDefaultRecoveryPlan(),
    // Always use static template for financial plan (3-2)
    financialPlan: BCP_FIXED_SECTIONS.FINANCIAL_PLAN_3_2,
    stockpilePlan: stockpilePlan || getDefaultStockpilePlan(),
    alternativeMeasures: alternativeMeasures || getDefaultAlternativeMeasures(),
    // Use extracted value if available, otherwise use default template (5-1 is editable)
    activationCriteria: activationCriteria || BCP_FIXED_SECTIONS.ACTIVATION_CRITERIA_5_1,
    // Always use static template for emergency organization (6-1)
    emergencyOrganization: BCP_FIXED_SECTIONS.EMERGENCY_RESPONSE_6_1,
    // Always use static template for roles and responsibilities (6-2)
    rolesResponsibilities: BCP_FIXED_SECTIONS.ROLES_RESPONSIBILITIES_6_2,
    // Always use static template for communication methods (6-3)
    communicationMethods: BCP_FIXED_SECTIONS.COMMUNICATION_METHODS_6_3,
    // Always use static template for regular training (7-1)
    regularTraining: BCP_FIXED_SECTIONS.REGULAR_TRAINING_7_1,
    // Always use static template for BCP review (7-2)
    bcpReview: BCP_FIXED_SECTIONS.BCP_REVIEW_7_2,
    // Always use static template for employee education (7-3)
    employeeEducation: BCP_FIXED_SECTIONS.EMPLOYEE_EDUCATION_7_3,
  };
};

/**
 * Check if extracted content is invalid (e.g., contains template notes)
 */
const isInvalidContent = (content: string | null): boolean => {
  if (!content) return true;
  if (content.length < 20) return true; // Too short
  if (content.includes("静的テンプレート")) return true;
  if (content.includes("このセクションは")) return true;
  if (content.includes("【注意】")) return true;
  if (content.includes("申し訳ありません")) return true;
  if (content.includes("生成しません")) return true;
  return false;
};

/**
 * Extract company information from various sources
 */
export const extractCompanyInfo = (
  extractedInfo: ExtractedCompanyInfo,
): Partial<BCPFormData> => {
  // Format employee count with unit if not present
  const formatEmployeeCount = (employees: string | undefined): string => {
    if (!employees) return "";
    // Check if unit is already present
    if (/名|人/.test(employees)) return employees;
    // Add unit if it's just a number
    return employees.match(/^\d+$/) ? `${employees}名` : employees;
  };

  return {
    companyInfo:
      `企業名：${extractedInfo.company_name || ""}\n` +
      `代表者：${extractedInfo.representative || ""}\n` +
      `所在地：${extractedInfo.address || ""}\n` +
      `TEL：${extractedInfo.tel || ""}\n` +
      `FAX：${extractedInfo.fax || ""}\n` +
      `事業内容：${extractedInfo.business_content || ""}\n` +
      `従業員数：${formatEmployeeCount(extractedInfo.employees)}\n` +
      `主な取引先：${extractedInfo.clients?.join("、") || ""}`,
  };
};

/**
 * Validate BCP sections for completeness
 */
export const validateBCPSections = (
  formData: BCPFormData,
): { field: string; message: string }[] => {
  const errors: { field: string; message: string }[] = [];

  const criticalSections = [
    {
      field: "companyInfo",
      value: formData.companyInfo,
      minLength: 30,
      name: "企業の基本情報",
    },
    {
      field: "continuityPolicy",
      value: formData.continuityPolicy,
      minLength: 50,
      name: "事業継続の基本方針",
    },
    {
      field: "riskAnalysis",
      value: formData.riskAnalysis,
      minLength: 50,
      name: "リスクの特定",
    },
    {
      field: "criticalBusinesses",
      value: formData.criticalBusinesses,
      minLength: 30,
      name: "重要業務の特定",
    },
  ];

  criticalSections.forEach((section) => {
    if (!section.value || section.value.length < section.minLength) {
      errors.push({
        field: section.field,
        message: `${section.name}は${section.minLength}文字以上で入力してください`,
      });
    }
  });

  return errors;
};

// Helper functions for building fallback content
const buildFallbackCompanyInfo = (
  extractedInfo: ExtractedCompanyInfo,
  filename: string,
  facilityInfo: string,
  employeeInfo: string,
  businessScope: string,
): string => {
  // Format employee count with unit if not present
  const formatEmployeeCount = (employees: string | undefined): string => {
    if (!employees) return "約100名";
    // Check if unit is already present
    if (/名|人/.test(employees)) return employees;
    // Add unit if it's just a number
    return employees.match(/^\d+$/) ? `${employees}名` : employees;
  };

  return (
    `企業名：${extractedInfo.company_name || filename}\n` +
    `代表者：代表取締役社長\n` +
    `所在地：${extractedInfo.address || "東京都"}\n` +
    `TEL：${extractedInfo.tel || "03-XXXX-XXXX"}\n` +
    `FAX：${extractedInfo.fax || "03-XXXX-XXXX"}\n` +
    `事業内容：${businessScope || (filename.includes("製造") || filename.includes("物産") ? "製造業（工作機械、精密機器、電子部品等の製造・販売）" : "各種商品の製造・販売")}\n` +
    `従業員数：${formatEmployeeCount(extractedInfo.employees)}\n` +
    `主な取引先：${extractedInfo.clients && extractedInfo.clients.length > 0 ? extractedInfo.clients.join("、") : "国内大手メーカー（製品供給）、商社・代理店（販売チャネル）"}\n` +
    `工場・施設概要：${facilityInfo || `本社・工場（${extractedInfo.address || "東京都"}）、主要設備：製造設備、検査装置、倉庫・保管設備`}`
  );
};

// Default content functions
const getDefaultContinuityPolicy = () =>
  "安全確保方針：従業員の安全を最優先\n早期復旧方針：迅速な事業再開\n社会的責任：地域社会への貢献";
const getDefaultRiskAnalysis = () =>
  "1. 自然災害（地震、台風、洪水等）\n2. 人為的災害（火災、爆発等）\n3. 停電・インフラ障害\n4. パンデミック";
const getDefaultCriticalBusinesses = () =>
  "【第一優先事業】\n" +
  "事業名: [主力製品・サービス名を記載してください]\n" +
  "例: 精密部品製造事業、システム開発事業、食品小売事業\n" +
  "選定理由:\n" +
  "・主力事業で売上の大部分を占める\n" +
  "・顧客への供給責任が大きい\n\n" +
  "【第二優先事業】\n" +
  "事業名: [第二優先の製品・サービス名を記載してください]\n" +
  "選定理由:\n" +
  "・取引先の製品完成に不可欠\n" +
  "・停止すると出荷遅延や顧客満足度低下につながる";
const getDefaultRecoveryPlan = () =>
  "製造業務：24時間以内\n品質管理：48時間以内";
// 固定セクションは BCP_FIXED_SECTIONS から取得
const getDefaultStockpilePlan = () =>
  "飲料水：3日分\n非常食：3日分\n燃料：発電機用";
const getDefaultAlternativeMeasures = () =>
  "リモートワーク体制：管理業務\n代替拠点：協力工場での生産";
// 固定セクションの関数は削除し、BCP_FIXED_SECTIONS を直接使用
